import { useState, useMemo } from 'react';

interface UsePaginationOptions {
  /** Numero iniziale di elementi per pagina */
  initialItemsPerPage?: number;
  /** Pagina iniziale (default: 1) */
  initialPage?: number;
}

interface PaginationInfo {
  /** Pagina corrente (1-based) */
  currentPage: number;
  /** Numero di elementi per pagina */
  itemsPerPage: number;
  /** Numero totale di pagine */
  totalPages: number;
  /** Numero totale di elementi */
  totalItems: number;
  /** Indice del primo elemento della pagina corrente (1-based) */
  startItem: number;
  /** Indice dell'ultimo elemento della pagina corrente */
  endItem: number;
  /** Se è possibile andare alla pagina precedente */
  canGoPrevious: boolean;
  /** Se è possibile andare alla pagina successiva */
  canGoNext: boolean;
}

interface UsePaginationReturn<T> {
  /** Dati paginati per la pagina corrente */
  paginatedData: T[];
  /** Informazioni sulla paginazione */
  paginationInfo: PaginationInfo;
  /** Vai a una pagina specifica */
  goToPage: (page: number) => void;
  /** Vai alla prima pagina */
  goToFirstPage: () => void;
  /** Vai alla pagina precedente */
  goToPreviousPage: () => void;
  /** Vai alla pagina successiva */
  goToNextPage: () => void;
  /** Vai all'ultima pagina */
  goToLastPage: () => void;
  /** Cambia il numero di elementi per pagina */
  changeItemsPerPage: (newItemsPerPage: number) => void;
  /** Reset alla prima pagina (utile dopo filtri) */
  resetToFirstPage: () => void;
}

/**
 * Hook personalizzato per gestire la paginazione di array di dati
 * @param data - Array di dati da paginare
 * @param options - Opzioni di configurazione
 * @returns Oggetto con dati paginati e funzioni per gestire la paginazione
 */
export const usePagination = <T>(
  data: T[],
  options: UsePaginationOptions = {}
): UsePaginationReturn<T> => {
  const {
    initialItemsPerPage = 10,
    initialPage = 1
  } = options;

  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState<number>(initialItemsPerPage);

  // Calcola informazioni di paginazione
  const paginationInfo = useMemo((): PaginationInfo => {
    const totalItems = data.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return {
      currentPage,
      itemsPerPage,
      totalPages,
      totalItems,
      startItem,
      endItem,
      canGoPrevious: currentPage > 1,
      canGoNext: currentPage < totalPages
    };
  }, [data.length, currentPage, itemsPerPage]);

  // Calcola dati paginati
  const paginatedData = useMemo((): T[] => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, itemsPerPage]);

  // Funzioni di navigazione
  const goToPage = (page: number) => {
    const { totalPages } = paginationInfo;
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
  };

  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const goToPreviousPage = () => {
    if (paginationInfo.canGoPrevious) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToNextPage = () => {
    if (paginationInfo.canGoNext) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToLastPage = () => {
    setCurrentPage(paginationInfo.totalPages);
  };

  const changeItemsPerPage = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset alla prima pagina
  };

  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  return {
    paginatedData,
    paginationInfo,
    goToPage,
    goToFirstPage,
    goToPreviousPage,
    goToNextPage,
    goToLastPage,
    changeItemsPerPage,
    resetToFirstPage
  };
};

export default usePagination;